// ============================================================
// LinuxLogs.jsx
//
// Branded, responsive course content page for the
// "Linux Logs & Monitoring" lesson.
//
// Brand palette (consistent across all lesson pages):
//   Background  → #100720  (deep purple-black)
//   Primary     → goldenrod / #DAA520
//   Accent      → #FFD700  (bright gold highlights)
//   Text        → #FFFFFF  (white)
//   Surface     → rgba(255,255,255,0.04) with gold borders
//
// Built with:
//   - Material UI (MUI v5) for layout, typography & icons
//   - Fully responsive (xs → xl breakpoints)
//   - Heavily commented for junior developers
// ============================================================

import React, { useState } from "react";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Collapse,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import ArticleIcon             from "@mui/icons-material/Article";
import SearchIcon              from "@mui/icons-material/Search";
import TerminalIcon            from "@mui/icons-material/Terminal";
import MemoryIcon              from "@mui/icons-material/Memory";
import LayersIcon              from "@mui/icons-material/Layers";
import FolderOpenIcon          from "@mui/icons-material/FolderOpen";
import FilterListIcon          from "@mui/icons-material/FilterList";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import SecurityIcon            from "@mui/icons-material/Security";
import BuildIcon               from "@mui/icons-material/Build";
import SettingsIcon            from "@mui/icons-material/Settings";
import StorageIcon             from "@mui/icons-material/Storage";
import SpeedIcon               from "@mui/icons-material/Speed";
import DnsIcon                 from "@mui/icons-material/Dns";
import BugReportIcon           from "@mui/icons-material/BugReport";
import LinkIcon                from "@mui/icons-material/Link";
import ExpandMoreIcon          from "@mui/icons-material/ExpandMore";
import ExpandLessIcon          from "@mui/icons-material/ExpandLess";
import ContentCopyIcon         from "@mui/icons-material/ContentCopy";
import DoneIcon                from "@mui/icons-material/Done";
import CheckCircleOutlineIcon  from "@mui/icons-material/CheckCircleOutline";
import CheckCircleIcon         from "@mui/icons-material/CheckCircle";
import LightbulbIcon           from "@mui/icons-material/Lightbulb";
import StarIcon                from "@mui/icons-material/Star";
import SchoolIcon              from "@mui/icons-material/School";
import WarningAmberIcon        from "@mui/icons-material/WarningAmber";
import AutorenewIcon           from "@mui/icons-material/Autorenew";
import MonitorHeartIcon        from "@mui/icons-material/MonitorHeart";
import VisibilityIcon          from "@mui/icons-material/Visibility";
import LockIcon                from "@mui/icons-material/Lock";
import HttpIcon                from "@mui/icons-material/Http";

// ── Brand tokens ──────────────────────────────────────────
// Centralise all colours — change here to retheme everything.
const BRAND = {
    bg:            "#233D4D",
    surface:       "rgba(255,255,255,0.04)",
    border:        "rgba(218,165,32,0.25)",
    borderHover:   "rgba(218,165,32,0.6)",
    gold:          "#DAA520",
    goldBright:    "#FFD700",
    goldGlow:      "rgba(218,165,32,0.15)",
    white:         "#FFFFFF",
    muted:         "rgba(255,255,255,0.55)",
    codeBg:        "rgba(0,0,0,0.45)",
    codeText:      "#FFD700",
    danger:        "rgba(255,80,80,0.12)",
    dangerBorder:  "rgba(255,80,80,0.35)",
    dangerText:    "#ff8080",
    success:       "rgba(39,201,63,0.10)",
    successBorder: "rgba(39,201,63,0.35)",
    successText:   "#27c93f",
    // Log level colours — mirrors real syslog severity colours
    levelEmerg:    "#ff4444",
    levelAlert:    "#ff6b35",
    levelCrit:     "#ff8c00",
    levelErr:      "#ffa500",
    levelWarn:     "#ffd700",
    levelNotice:   "#60a5fa",
    levelInfo:     "#4ade80",
    levelDebug:    "#a78bfa",
};

// ── Shared heading style ──────────────────────────────────
const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── MUI TableCell style helper ────────────────────────────
const tcStyle = (extra = {}) => ({
    borderColor: BRAND.border,
    color: BRAND.muted,
    fontSize: 13,
    py: 1.4,
    ...extra,
});

// ============================================================
//  SUB-COMPONENT: SectionTitle
//  Gold icon circle + heading — reused by every section.
//  Props:
//    icon  – MUI icon element
//    label – heading text string
// ============================================================
function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box
                sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    background: BRAND.goldGlow,
                    border: `1px solid ${BRAND.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                }}
            >
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ConceptCard
//  Hoverable card for one logging concept.
// ============================================================
function ConceptCard({ icon, title, description }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                height: "100%",
                background: BRAND.surface,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 3,
                transition: "border-color 0.25s, box-shadow 0.25s",
                "&:hover": {
                    borderColor: BRAND.borderHover,
                    boxShadow: `0 0 20px ${BRAND.goldGlow}`,
                },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.2 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 22 } })}
                <Typography sx={{ fontFamily: "'Georgia', serif", fontWeight: 700, color: BRAND.white, fontSize: 14 }}>
                    {title}
                </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.65 }}>
                {description}
            </Typography>
        </Paper>
    );
}

// ============================================================
//  SUB-COMPONENT: CodeBlock
//  Terminal-style block with copy-to-clipboard.
//  Props:
//    lines – array of { cmd: string, comment?: string }
// ============================================================
function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box
            sx={{
                background: BRAND.codeBg,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2,
                overflow: "hidden",
                mb: 2,
            }}
        >
            {/* ── Terminal title bar ── */}
            <Box
                sx={{
                    px: 2, py: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: `1px solid ${BRAND.border}`,
                    background: "rgba(0,0,0,0.3)",
                }}
            >
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy"} placement="top">
                    <Box
                        onClick={handleCopy}
                        sx={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: copied ? BRAND.goldBright : BRAND.muted,
                            fontSize: 12,
                            transition: "color 0.2s",
                            "&:hover": { color: BRAND.gold },
                        }}
                    >
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>

            {/* ── Command lines ── */}
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && (
                            <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.3 }}>
                                # {comment}
                            </Typography>
                        )}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 12, md: 13 }, color: BRAND.codeText, wordBreak: "break-all" }}>
                            {cmd}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: WarningBox
//  Red-tinted panel for important warnings.
// ============================================================
function WarningBox({ children }) {
    return (
        <Box
            sx={{
                display: "flex", alignItems: "flex-start", gap: 1.5,
                p: 2, mb: 2,
                background: BRAND.danger,
                border: `1px solid ${BRAND.dangerBorder}`,
                borderRadius: 2,
            }}
        >
            <WarningAmberIcon sx={{ color: BRAND.dangerText, flexShrink: 0, mt: 0.1 }} />
            <Typography sx={{ color: BRAND.dangerText, fontSize: 13, lineHeight: 1.7 }}>
                {children}
            </Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: SuccessBox
//  Green-tinted best-practice tip box.
// ============================================================
function SuccessBox({ children }) {
    return (
        <Box
            sx={{
                display: "flex", alignItems: "flex-start", gap: 1.5,
                p: 2, mb: 2,
                background: BRAND.success,
                border: `1px solid ${BRAND.successBorder}`,
                borderRadius: 2,
            }}
        >
            <CheckCircleIcon sx={{ color: BRAND.successText, flexShrink: 0, mt: 0.1 }} />
            <Typography sx={{ color: BRAND.successText, fontSize: 13, lineHeight: 1.7 }}>
                {children}
            </Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ExpandableExample
//  Accordion for optional deeper content.
// ============================================================
function ExpandableExample({ title, icon, children }) {
    const [open, setOpen] = useState(false);

    return (
        <Box
            sx={{
                border: `1px solid ${open ? BRAND.borderHover : BRAND.border}`,
                borderRadius: 2.5,
                overflow: "hidden",
                mb: 1.5,
                transition: "border-color 0.25s",
            }}
        >
            <Box
                onClick={() => setOpen((p) => !p)}
                sx={{
                    px: 2.5, py: 1.8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    background: open ? BRAND.goldGlow : BRAND.surface,
                    transition: "background 0.25s",
                    "&:hover": { background: BRAND.goldGlow },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                    <Typography sx={{ color: BRAND.white, fontWeight: 600, fontSize: 14 }}>
                        {title}
                    </Typography>
                </Box>
                {open ? <ExpandLessIcon sx={{ color: BRAND.gold }} /> : <ExpandMoreIcon sx={{ color: BRAND.muted }} />}
            </Box>

            {/* Smooth MUI Collapse animation */}
            <Collapse in={open}>
                <Box sx={{ px: 2.5, py: 2, background: "rgba(0,0,0,0.25)", borderTop: `1px solid ${BRAND.border}` }}>
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: LogFileRow
//  One row in the log files reference table.
//  Props:
//    path     – log file path
//    distro   – which distro uses it (or "All")
//    category – type badge label
//    color    – badge accent colour
//    desc     – plain-English description
// ============================================================
function LogFileRow({ path, distro, category, color, desc }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: { xs: 1, sm: 1.5 },
                py: 1.5,
                px: 1.5,
                borderBottom: `1px solid ${BRAND.border}`,
                "&:last-child": { borderBottom: "none" },
                transition: "background 0.2s",
                "&:hover": { background: "rgba(255,255,255,0.02)" },
            }}
        >
            {/* Category chip */}
            <Chip
                label={category}
                size="small"
                sx={{
                    background: `${color}18`,
                    border: `1px solid ${color}55`,
                    color,
                    fontFamily: "monospace",
                    fontSize: 10,
                    height: 20,
                    flexShrink: 0,
                    alignSelf: "center",
                    letterSpacing: 0.5,
                }}
            />

            {/* File path in gold monospace */}
            <Typography
                sx={{
                    fontFamily: "monospace",
                    fontSize: { xs: 12, md: 13 },
                    color: BRAND.goldBright,
                    fontWeight: 600,
                    minWidth: { xs: "100%", sm: 220 },
                    flexShrink: 0,
                    wordBreak: "break-all",
                }}
            >
                {path}
            </Typography>

            {/* Distro badge (small, muted) */}
            <Typography sx={{ fontFamily: "monospace", fontSize: 11, color: BRAND.muted, flexShrink: 0, alignSelf: "center" }}>
                [{distro}]
            </Typography>

            {/* Description */}
            <Typography sx={{ fontSize: 13, color: BRAND.muted, flex: 1, lineHeight: 1.6 }}>
                {desc}
            </Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: SeverityLegend
//  Displays all 8 syslog severity levels as colour-coded chips.
//  Used in the "Log Levels" part of the journalctl section.
// ============================================================
function SeverityLegend() {
    const levels = [
        { num: 0, name: "emerg",   label: "System unusable",            color: BRAND.levelEmerg  },
        { num: 1, name: "alert",   label: "Immediate action required",  color: BRAND.levelAlert  },
        { num: 2, name: "crit",    label: "Critical conditions",        color: BRAND.levelCrit   },
        { num: 3, name: "err",     label: "Error conditions",           color: BRAND.levelErr    },
        { num: 4, name: "warning", label: "Warning conditions",         color: BRAND.levelWarn   },
        { num: 5, name: "notice",  label: "Normal but significant",     color: BRAND.levelNotice },
        { num: 6, name: "info",    label: "Informational messages",     color: BRAND.levelInfo   },
        { num: 7, name: "debug",   label: "Debug-level messages",       color: BRAND.levelDebug  },
    ];

    return (
        <Box
            sx={{
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2,
                overflow: "hidden",
                mb: 3,
            }}
        >
            {/* Header */}
            <Box sx={{ px: 2, py: 1, background: BRAND.goldGlow, borderBottom: `1px solid ${BRAND.border}`, display: "flex", gap: 2 }}>
                {["Level", "Keyword", "Meaning"].map((h) => (
                    <Typography key={h} sx={{ fontSize: 11, color: BRAND.gold, fontFamily: "monospace", letterSpacing: 1, textTransform: "uppercase", flex: h === "Meaning" ? 1 : "none", minWidth: h === "Level" ? 50 : h === "Keyword" ? 90 : "auto" }}>
                        {h}
                    </Typography>
                ))}
            </Box>

            {/* One row per severity level */}
            {levels.map(({ num, name, label, color }) => (
                <Box
                    key={name}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        px: 2,
                        py: 1.1,
                        borderBottom: `1px solid ${BRAND.border}`,
                        "&:last-child": { borderBottom: "none" },
                        "&:hover": { background: "rgba(255,255,255,0.02)" },
                        transition: "background 0.2s",
                    }}
                >
                    {/* Numeric level */}
                    <Typography sx={{ fontFamily: "monospace", fontSize: 14, fontWeight: 800, color, minWidth: 50 }}>
                        {num}
                    </Typography>

                    {/* Keyword chip */}
                    <Chip
                        label={name}
                        size="small"
                        sx={{
                            background: `${color}18`,
                            border: `1px solid ${color}55`,
                            color,
                            fontFamily: "monospace",
                            fontSize: 11,
                            height: 22,
                            minWidth: 80,
                        }}
                    />

                    {/* Human label */}
                    <Typography sx={{ fontSize: 13, color: BRAND.muted, flex: 1 }}>
                        {label}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ResourceLink
//  Styled external-link row.
// ============================================================
function ResourceLink({ href, label, sub }) {
    return (
        <Box
            component="a"
            href={href}
            target="_blank"
            rel="noreferrer"
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2, mb: 1.2,
                textDecoration: "none",
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2,
                background: BRAND.surface,
                transition: "border-color 0.25s, box-shadow 0.25s",
                "&:hover": {
                    borderColor: BRAND.borderHover,
                    boxShadow: `0 0 16px ${BRAND.goldGlow}`,
                },
            }}
        >
            <LinkIcon sx={{ color: BRAND.gold, fontSize: 20, flexShrink: 0 }} />
            <Box>
                <Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 14, lineHeight: 1.3 }}>
                    {label}
                </Typography>
                {sub && <Typography sx={{ color: BRAND.muted, fontSize: 12, mt: 0.3 }}>{sub}</Typography>}
            </Box>
        </Box>
    );
}

// ============================================================
//  MAIN COMPONENT: LinuxLogs
//  Full "Linux Logs & Monitoring" lesson page.
// ============================================================
export default function LinuxLogs() {
    return (
        <Box
            sx={{
                minHeight: "100vh",
                background: BRAND.bg,
                // Subtle diagonal texture — consistent across all lesson pages
                backgroundImage: `repeating-linear-gradient(
          -45deg,
          rgba(218,165,32,0.02) 0px,
          rgba(218,165,32,0.02) 1px,
          transparent 1px,
          transparent 12px
        )`,
                py: { xs: 4, md: 7 },
                px: { xs: 2, sm: 3 },
            }}
        >
            <Container maxWidth="lg">

                {/* ══════════════════════════════════════════════
            HERO HEADER
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>

                    <Chip
                        icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />}
                        label="Systems · Module 09"
                        sx={{
                            mb: 2.5,
                            background: BRAND.goldGlow,
                            border: `1px solid ${BRAND.border}`,
                            color: BRAND.gold,
                            fontFamily: "monospace",
                            fontSize: 11,
                            letterSpacing: 1,
                            textTransform: "uppercase",
                        }}
                    />

                    <Typography
                        variant="h3"
                        sx={{
                            ...sectionHeading,
                            fontSize: { xs: 26, sm: 34, md: 42 },
                            mb: 2,
                            "& span": {
                                background: `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldBright})`,
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                            },
                        }}
                    >
                        Linux <span>Logs</span> &amp; Monitoring
                    </Typography>

                    <Typography
                        sx={{
                            color: BRAND.muted,
                            fontSize: { xs: 14, md: 16 },
                            maxWidth: 680,
                            lineHeight: 1.8,
                            mx: { xs: "auto", md: 0 },
                            mb: 3,
                        }}
                    >
                        Logs are the memory of your system. Every login, error, crash, service start,
                        security violation, and hardware event is recorded somewhere in Linux. Knowing
                        where to look — and how to filter, follow, and query logs — is the core diagnostic
                        skill that separates administrators who can fix problems in minutes from those
                        who spend hours guessing.
                    </Typography>

                    {/* Quick stats */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: { xs: "center", md: "flex-start" } }}>
                        {[
                            { label: "~2.5 hours", sub: "read time"   },
                            { label: "Intermediate", sub: "level"      },
                            { label: "3 systems",  sub: "covered"      },
                        ].map(({ label, sub }) => (
                            <Box
                                key={label}
                                sx={{
                                    textAlign: "center",
                                    background: BRAND.surface,
                                    border: `1px solid ${BRAND.border}`,
                                    borderRadius: 2,
                                    px: 2.5, py: 1.2,
                                }}
                            >
                                <Typography sx={{ color: BRAND.goldBright, fontWeight: 800, fontSize: 15 }}>{label}</Typography>
                                <Typography sx={{ color: BRAND.muted, fontSize: 11 }}>{sub}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* ══════════════════════════════════════════════
            SECTION 1 – KEY CONCEPTS
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<LayersIcon />} label="Key Concepts" />

                    <Grid container spacing={2}>
                        {[
                            {
                                icon: <ArticleIcon />,
                                title: "Traditional Log Files",
                                description:
                                    "Legacy logging writes plain-text files to /var/log/. Each service writes to its own file. They are human-readable, grep-able, and work without systemd. The rsyslog or syslog-ng daemon collects and routes these messages.",
                            },
                            {
                                icon: <StorageIcon />,
                                title: "systemd Journal",
                                description:
                                    "systemd's journald collects logs from all services, the kernel, and the boot process into a structured binary database. It persists across reboots, supports rich filtering, and is queried with journalctl.",
                            },
                            {
                                icon: <FilterListIcon />,
                                title: "Syslog Severity Levels",
                                description:
                                    "Every log message has a severity from 0 (emerg) to 7 (debug). Knowing the levels helps you filter the noise — in production you typically only care about levels 0–4 (emerg through warning).",
                            },
                            {
                                icon: <AutorenewIcon />,
                                title: "Log Rotation",
                                description:
                                    "Log files grow forever unless rotated. logrotate compresses, renames, and deletes old log files on a schedule defined in /etc/logrotate.conf and /etc/logrotate.d/. Without it, /var/log fills up and crashes services.",
                            },
                            {
                                icon: <MonitorHeartIcon />,
                                title: "Real-Time Monitoring",
                                description:
                                    "tail -f and journalctl -f stream new log entries as they arrive — essential when watching a service start, debugging a live issue, or monitoring login attempts during an incident.",
                            },
                            {
                                icon: <SecurityIcon />,
                                title: "Security Auditing",
                                description:
                                    "auth.log and secure record every login attempt, sudo use, SSH connection, and PAM event. Combined with journalctl -u sshd and fail2ban, these logs are your primary intrusion detection interface.",
                            },
                        ].map((card) => (
                            <Grid item xs={12} sm={6} md={4} key={card.title}>
                                <ConceptCard {...card} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 2 – TRADITIONAL LOG FILES
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<FolderOpenIcon />} label="Traditional Log Files" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        These plain-text files under <code style={{ color: BRAND.goldBright }}>/var/log/</code>{" "}
                        are managed by the <strong style={{ color: BRAND.white }}>rsyslog</strong> or{" "}
                        <strong style={{ color: BRAND.white }}>syslog-ng</strong> daemon.
                        On systemd-based systems many of these are generated by forwarding the journal to
                        plain text. Colour-coded by category for fast scanning.
                    </Typography>

                    {/* Legend */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.2, mb: 2.5 }}>
                        {[
                            { label: "System",    color: BRAND.goldBright },
                            { label: "Auth",      color: "#f472b6"         },
                            { label: "Kernel",    color: "#60a5fa"         },
                            { label: "Service",   color: "#4ade80"         },
                            { label: "App",       color: "#fb923c"         },
                        ].map(({ label, color }) => (
                            <Box
                                key={label}
                                sx={{
                                    display: "flex", alignItems: "center", gap: 0.7,
                                    background: `${color}12`,
                                    border: `1px solid ${color}40`,
                                    borderRadius: 20,
                                    px: 1.2, py: 0.4,
                                }}
                            >
                                <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: color }} />
                                <Typography sx={{ fontSize: 11, color, fontFamily: "monospace" }}>{label}</Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* Log file reference table */}
                    <Box sx={{ border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 3 }}>
                        {/* Column headers */}
                        <Box
                            sx={{
                                display: { xs: "none", sm: "flex" },
                                gap: 1.5, px: 1.5, py: 1,
                                background: BRAND.goldGlow,
                                borderBottom: `1px solid ${BRAND.border}`,
                            }}
                        >
                            {["Cat", "Path", "Distro", "Description"].map((h) => (
                                <Typography key={h} sx={{ fontSize: 11, color: BRAND.gold, fontFamily: "monospace", letterSpacing: 1, textTransform: "uppercase" }}>
                                    {h}
                                </Typography>
                            ))}
                        </Box>

                        {[
                            { path: "/var/log/syslog",            distro: "Debian/Ubuntu",   category: "System",  color: BRAND.goldBright, desc: "General system messages from the kernel and services — the main log to check first on Ubuntu." },
                            { path: "/var/log/messages",           distro: "RHEL/Fedora/Arch",category: "System",  color: BRAND.goldBright, desc: "Equivalent of syslog on Red Hat-based systems. General-purpose system activity log." },
                            { path: "/var/log/auth.log",           distro: "Debian/Ubuntu",   category: "Auth",    color: "#f472b6",        desc: "All authentication events: SSH logins, sudo usage, PAM, su, and failed login attempts." },
                            { path: "/var/log/secure",             distro: "RHEL/Fedora",     category: "Auth",    color: "#f472b6",        desc: "Equivalent of auth.log on Red Hat-based systems. Security and authentication events." },
                            { path: "/var/log/kern.log",           distro: "Debian/Ubuntu",   category: "Kernel",  color: "#60a5fa",        desc: "Kernel messages only — hardware detection, driver events, OOM kills. Subset of syslog." },
                            { path: "/var/log/dmesg",              distro: "All",             category: "Kernel",  color: "#60a5fa",        desc: "Kernel ring buffer captured at boot. Older — prefer the live dmesg command for current data." },
                            { path: "/var/log/boot.log",           distro: "RHEL/Fedora",     category: "System",  color: BRAND.goldBright, desc: "System boot process messages — service start/stop results during boot sequence." },
                            { path: "/var/log/cron",               distro: "RHEL/Fedora",     category: "Service", color: "#4ade80",        desc: "Cron job execution log — every job that ran, its exit status, and any errors." },
                            { path: "/var/log/mail.log",           distro: "Debian/Ubuntu",   category: "Service", color: "#4ade80",        desc: "Mail system activity (Postfix, Sendmail). Useful for debugging email delivery issues." },
                            { path: "/var/log/dpkg.log",           distro: "Debian/Ubuntu",   category: "System",  color: BRAND.goldBright, desc: "Package installation and removal history — every apt install, remove, and upgrade." },
                            { path: "/var/log/apt/history.log",    distro: "Debian/Ubuntu",   category: "System",  color: BRAND.goldBright, desc: "Human-readable apt command history with timestamps and package lists." },
                            { path: "/var/log/nginx/access.log",   distro: "All",             category: "App",     color: "#fb923c",        desc: "Every HTTP request to your Nginx web server: IP, method, URL, status code, size." },
                            { path: "/var/log/nginx/error.log",    distro: "All",             category: "App",     color: "#fb923c",        desc: "Nginx errors: missing files, permission denied, upstream failures, config errors." },
                            { path: "/var/log/apache2/access.log", distro: "Debian/Ubuntu",   category: "App",     color: "#fb923c",        desc: "Apache web server access log — same format as Nginx access log." },
                            { path: "/var/log/mysql/error.log",    distro: "All",             category: "App",     color: "#fb923c",        desc: "MySQL/MariaDB startup errors, query failures, and InnoDB storage engine events." },
                            { path: "/var/log/fail2ban.log",       distro: "All",             category: "Auth",    color: "#f472b6",        desc: "fail2ban IP ban/unban events. Shows which IPs are being blocked for brute-force attempts." },
                            { path: "/var/log/wtmp",               distro: "All",             category: "Auth",    color: "#f472b6",        desc: "Binary file logging all logins, logouts, and reboots. Read with the 'last' command." },
                            { path: "/var/log/btmp",               distro: "All",             category: "Auth",    color: "#f472b6",        desc: "Binary file logging failed login attempts. Read with 'sudo lastb'." },
                        ].map((row) => (
                            <LogFileRow key={row.path} {...row} />
                        ))}
                    </Box>

                    {/* Viewing log files */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 Viewing Log Files
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ tail -f /var/log/syslog",                      comment: "Live-follow: stream new lines as they arrive (Ctrl+C to stop)" },
                            { cmd: "$ tail -n 100 /var/log/syslog",                  comment: "Show the last 100 lines" },
                            { cmd: "$ less /var/log/auth.log",                       comment: "Page through a log file (G = end, g = start, q = quit)" },
                            { cmd: "$ cat /var/log/dpkg.log",                        comment: "Print entire log to terminal (avoid on large files)" },
                            { cmd: "$ head -50 /var/log/syslog",                     comment: "Show only the first 50 lines (useful for boot logs)" },
                            { cmd: "$ wc -l /var/log/syslog",                        comment: "Count total lines in a log file" },
                            { cmd: "$ sudo last",                                     comment: "Show login/logout history from /var/log/wtmp" },
                            { cmd: "$ sudo lastb",                                    comment: "Show failed login attempts from /var/log/btmp" },
                            { cmd: "$ sudo last -n 20",                              comment: "Show only the 20 most recent login events" },
                        ]}
                    />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 Searching & Filtering Logs
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ grep 'error' /var/log/syslog",                 comment: "Find all lines containing 'error' (case-sensitive)" },
                            { cmd: "$ grep -i 'error\\|fail\\|warn' /var/log/syslog",comment: "Case-insensitive search for multiple patterns" },
                            { cmd: "$ grep -n 'Failed password' /var/log/auth.log",  comment: "-n shows line numbers next to each match" },
                            { cmd: "$ grep -c 'Failed password' /var/log/auth.log",  comment: "-c counts matching lines instead of printing them" },
                            { cmd: "$ grep -A 3 'segfault' /var/log/syslog",         comment: "-A 3 shows 3 lines AFTER each match for context" },
                            { cmd: "$ grep -B 2 'segfault' /var/log/syslog",         comment: "-B 2 shows 2 lines BEFORE each match" },
                            { cmd: "$ awk '/Jan 15/{print}' /var/log/syslog",        comment: "Print only lines from January 15th" },
                            { cmd: "$ zcat /var/log/syslog.2.gz | grep error",       comment: "Search inside a gzip-compressed rotated log file" },
                        ]}
                    />

                    <ExpandableExample title="Searching across multiple rotated log files" icon={<SearchIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            logrotate compresses old log files as <code style={{ color: BRAND.goldBright }}>syslog.1</code>,{" "}
                            <code style={{ color: BRAND.goldBright }}>syslog.2.gz</code>, etc. Use{" "}
                            <code style={{ color: BRAND.goldBright }}>zgrep</code> and{" "}
                            <code style={{ color: BRAND.goldBright }}>zcat</code> to search inside
                            compressed files without decompressing them first.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ zgrep 'Failed password' /var/log/auth.log*",   comment: "Search all auth.log files including compressed ones" },
                                { cmd: "$ zcat /var/log/syslog.*.gz | grep 'kernel'",    comment: "Search all compressed syslog archives for kernel messages" },
                                { cmd: "$ ls -lhS /var/log/",                            comment: "List log files sorted by size — find the big ones" },
                                { cmd: "$ du -sh /var/log/*",                            comment: "Show disk usage of each log file or directory" },
                                { cmd: "$ du -sh /var/log/ | sort -rh | head -10",       comment: "Find the top 10 largest log items" },
                            ]}
                        />
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 3 – JOURNALCTL
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StorageIcon />} label="journalctl — systemd Journal" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        <code style={{ color: BRAND.goldBright }}>journalctl</code> queries the systemd
                        journal — a structured, indexed log database. Unlike plain-text files, the journal
                        stores every field (service name, PID, hostname, timestamp, message) in a queryable
                        format. It supports rich time-based, service-based, and priority-based filtering.
                    </Typography>

                    {/* Syslog severity reference */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.5 }}>
                        🟡 Syslog Severity Levels
                    </Typography>
                    <SeverityLegend />

                    {/* Core journalctl commands */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 Basic Journal Queries
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ journalctl",                                    comment: "All journal entries — oldest first (very long, pipe to less)" },
                            { cmd: "$ journalctl | less",                             comment: "Page through the entire journal" },
                            { cmd: "$ journalctl -n 50",                              comment: "Show the 50 most recent entries" },
                            { cmd: "$ journalctl -f",                                 comment: "Follow: stream new entries in real time (like tail -f)" },
                            { cmd: "$ journalctl -e",                                 comment: "Jump to the end of the journal immediately" },
                            { cmd: "$ journalctl -xe",                                comment: "Jump to end with extra context — shows recent errors with details" },
                            { cmd: "$ journalctl --no-pager",                         comment: "Print all output to stdout without paginating (good for scripts)" },
                        ]}
                    />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 Filtering by Service (Unit)
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ journalctl -u ssh",                             comment: "All logs from the SSH service" },
                            { cmd: "$ journalctl -u nginx",                           comment: "All logs from the Nginx web server" },
                            { cmd: "$ journalctl -u nginx -f",                        comment: "Follow Nginx logs in real time" },
                            { cmd: "$ journalctl -u nginx -n 100",                    comment: "Last 100 Nginx log entries" },
                            { cmd: "$ journalctl -u nginx -u mysql",                  comment: "Logs from multiple units combined" },
                            { cmd: "$ journalctl _SYSTEMD_UNIT=cron.service",         comment: "Filter using the structured journal field directly" },
                        ]}
                    />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 Filtering by Time
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ journalctl -b",                                 comment: "Logs from the current boot only" },
                            { cmd: "$ journalctl -b -1",                              comment: "Logs from the previous boot (-2, -3 for earlier boots)" },
                            { cmd: "$ journalctl --list-boots",                       comment: "List all recorded boots with their IDs and timestamps" },
                            { cmd: `$ journalctl --since "1 hour ago"`,               comment: "Entries from the last hour" },
                            { cmd: `$ journalctl --since "2025-01-15 09:00" --until "2025-01-15 10:00"`, comment: "Entries within an exact time window" },
                            { cmd: `$ journalctl --since "today"`,                    comment: "All entries since midnight today" },
                            { cmd: `$ journalctl --since yesterday`,                  comment: "All entries since midnight yesterday" },
                        ]}
                    />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 Filtering by Priority
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ journalctl -p err",                             comment: "Show only errors (level 3) and above (0–3: emerg, alert, crit, err)" },
                            { cmd: "$ journalctl -p warning",                         comment: "Warnings and above (levels 0–4)" },
                            { cmd: "$ journalctl -p 0..3",                            comment: "Entries between priority 0 (emerg) and 3 (err)" },
                            { cmd: "$ journalctl -k -p err",                          comment: "Kernel errors only (-k = kernel messages)" },
                            { cmd: "$ journalctl -p err -b",                          comment: "Errors from the current boot — great first check after reboot" },
                        ]}
                    />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 Advanced Filtering & Output
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ journalctl -u nginx --since '1 hour ago' -p err", comment: "Combine unit + time + priority filters" },
                            { cmd: "$ journalctl _PID=1234",                          comment: "Logs from a specific process ID" },
                            { cmd: "$ journalctl _UID=1000",                          comment: "Logs generated by a specific user ID" },
                            { cmd: "$ journalctl -o json-pretty | head -40",          comment: "Output in pretty-printed JSON — useful for parsing" },
                            { cmd: "$ journalctl -o short-iso",                       comment: "Output with ISO timestamps instead of relative times" },
                            { cmd: "$ journalctl -g 'Failed password'",               comment: "-g = grep: filter by message text" },
                            { cmd: "$ journalctl --disk-usage",                       comment: "Show how much disk space the journal is using" },
                            { cmd: "$ sudo journalctl --vacuum-size=500M",            comment: "Trim journal to stay under 500 MB" },
                            { cmd: "$ sudo journalctl --vacuum-time=2weeks",          comment: "Delete journal entries older than 2 weeks" },
                        ]}
                    />

                    <ExpandableExample title="Configuring journal persistence and size limits" icon={<SettingsIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            By default the journal may be stored only in RAM (volatile). To persist logs across
                            reboots, configure <code style={{ color: BRAND.goldBright }}>/etc/systemd/journald.conf</code>.
                            You can also limit maximum size so it never fills the disk.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ sudo mkdir -p /var/log/journal",              comment: "Create this directory to enable persistent journal storage" },
                                { cmd: "$ sudo systemctl restart systemd-journald",     comment: "Restart journald to pick up the change" },
                                { cmd: "$ sudo nano /etc/systemd/journald.conf",        comment: "Edit journal configuration" },
                            ]}
                        />
                        <Box
                            sx={{
                                background: BRAND.codeBg,
                                border: `1px solid ${BRAND.border}`,
                                borderRadius: 2,
                                p: 2, mb: 2,
                                overflowX: "auto",
                            }}
                        >
                            {[
                                "[Journal]",
                                "Storage=persistent          # volatile | persistent | auto | none",
                                "Compress=yes               # Compress entries (saves significant space)",
                                "SystemMaxUse=500M          # Max total journal size on disk",
                                "SystemKeepFree=100M        # Always keep at least 100 MB free",
                                "MaxRetentionSec=2month     # Delete entries older than 2 months",
                                "MaxFileSec=1month          # Rotate journal files monthly",
                                "RateLimitInterval=30s      # Rate limit: max messages per interval",
                                "RateLimitBurst=10000       # Max 10000 messages per 30s per service",
                            ].map((line, i) => (
                                <Typography key={i} sx={{ fontFamily: "monospace", fontSize: 12, color: line.startsWith("[") ? BRAND.goldBright : line.startsWith("#") ? "rgba(255,255,255,0.3)" : BRAND.codeText, lineHeight: 1.85 }}>
                                    {line || "\u00A0"}
                                </Typography>
                            ))}
                        </Box>
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 4 – LOG ROTATION
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<AutorenewIcon />} label="Log Rotation with logrotate" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        Without rotation, log files grow without bound and eventually fill{" "}
                        <code style={{ color: BRAND.goldBright }}>/var/log/</code>, crashing services that
                        try to write to disk. <code style={{ color: BRAND.goldBright }}>logrotate</code>{" "}
                        runs daily (via cron or a systemd timer) and compresses, renames, and deletes
                        old log files based on rules in its configuration files.
                    </Typography>

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 logrotate Commands
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ cat /etc/logrotate.conf",                       comment: "View global logrotate defaults" },
                            { cmd: "$ ls /etc/logrotate.d/",                          comment: "Per-service rotation rules (nginx, apt, rsyslog, etc.)" },
                            { cmd: "$ cat /etc/logrotate.d/nginx",                    comment: "Inspect Nginx rotation config" },
                            { cmd: "$ sudo logrotate -d /etc/logrotate.conf",         comment: "Dry run: show what would be rotated without doing it" },
                            { cmd: "$ sudo logrotate -f /etc/logrotate.conf",         comment: "Force rotate all logs now (ignores time since last rotation)" },
                            { cmd: "$ sudo logrotate -f /etc/logrotate.d/nginx",      comment: "Force rotate Nginx logs only" },
                            { cmd: "$ cat /var/lib/logrotate/status",                 comment: "See when each log was last rotated" },
                        ]}
                    />

                    <ExpandableExample title="Writing a custom logrotate configuration" icon={<BuildIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            Drop a file in <code style={{ color: BRAND.goldBright }}>/etc/logrotate.d/</code>{" "}
                            to define rotation rules for your application logs. The filename can be anything —
                            logrotate reads all files in that directory.
                        </Typography>
                        <Box
                            sx={{
                                background: BRAND.codeBg,
                                border: `1px solid ${BRAND.border}`,
                                borderRadius: 2,
                                p: 2, mb: 2,
                                overflowX: "auto",
                            }}
                        >
                            {[
                                { line: "# /etc/logrotate.d/myapp",                    isComment: true  },
                                { line: "/var/log/myapp/*.log {"                                         },
                                { line: "    daily                    # Rotate daily",  isComment: false },
                                { line: "    rotate 14               # Keep 14 rotated copies",          },
                                { line: "    compress                # gzip old log files",              },
                                { line: "    delaycompress           # Keep yesterday uncompressed",     },
                                { line: "    missingok              # Don't error if log file missing",  },
                                { line: "    notifempty             # Don't rotate if file is empty",    },
                                { line: "    create 0640 www-data www-data  # Create new log with these permissions", },
                                { line: "    sharedscripts          # Run postrotate script only once",  },
                                { line: "    postrotate",                                                 },
                                { line: "        systemctl reload myapp 2>/dev/null || true",            },
                                { line: "    endscript",                                                  },
                                { line: "}",                                                              },
                            ].map((entry, i) => (
                                <Typography key={i} sx={{ fontFamily: "monospace", fontSize: 12, color: entry.isComment ? "rgba(255,255,255,0.3)" : BRAND.codeText, lineHeight: 1.9 }}>
                                    {entry.line || "\u00A0"}
                                </Typography>
                            ))}
                        </Box>
                        <SuccessBox>
                            The <code>postrotate</code> script sends a reload signal to your application
                            so it reopens its log file handle after rotation. Without this, the app keeps
                            writing to the renamed/compressed old file.
                        </SuccessBox>
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 5 – REAL-TIME MONITORING TOOLS
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<MonitorHeartIcon />} label="Real-Time System Monitoring" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        Beyond logs, Linux ships with powerful real-time monitoring tools that show
                        running processes, resource usage, network traffic, and disk I/O as they happen.
                    </Typography>

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 Process & Resource Monitors
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ top",                                           comment: "Interactive process monitor — CPU, RAM, load average (q to quit)" },
                            { cmd: "$ htop",                                          comment: "Better top: colour, mouse support, tree view (install: sudo apt install htop)" },
                            { cmd: "$ ps aux",                                        comment: "Snapshot of all running processes with CPU and memory %" },
                            { cmd: "$ ps aux --sort=-%mem | head -10",               comment: "Top 10 processes by memory usage" },
                            { cmd: "$ ps aux --sort=-%cpu | head -10",               comment: "Top 10 processes by CPU usage" },
                            { cmd: "$ vmstat 2 10",                                   comment: "System stats (CPU, memory, I/O, swap) every 2s, 10 times" },
                            { cmd: "$ free -h",                                       comment: "RAM and swap usage in human-readable format" },
                            { cmd: "$ uptime",                                        comment: "System uptime and 1/5/15-minute load averages" },
                        ]}
                    />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 Disk & I/O Monitoring
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ iostat -x 2",                                   comment: "Extended disk I/O stats every 2 seconds (install: sysstat)" },
                            { cmd: "$ iotop",                                         comment: "Live per-process disk I/O (like top for disk, install: sudo apt install iotop)" },
                            { cmd: "$ df -h",                                         comment: "Disk space usage for all mounted filesystems" },
                            { cmd: "$ du -sh /var/log/* | sort -rh | head -10",       comment: "Top 10 largest items in /var/log" },
                            { cmd: "$ lsof | grep deleted",                           comment: "Find deleted files still held open (a common cause of 'disk full' surprises)" },
                        ]}
                    />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 Network Monitoring
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ ss -tulnp",                                     comment: "Show all listening ports with process names (modern netstat)" },
                            { cmd: "$ netstat -tulnp",                                comment: "Same with older netstat command (install net-tools)" },
                            { cmd: "$ iftop",                                         comment: "Live per-connection bandwidth monitor (install: sudo apt install iftop)" },
                            { cmd: "$ nload",                                         comment: "Simple in/out bandwidth graph per interface (sudo apt install nload)" },
                            { cmd: "$ tcpdump -i eth0 -n",                            comment: "Capture live packets on eth0 (powerful, low-level)" },
                            { cmd: "$ ss -s",                                         comment: "Socket statistics summary" },
                        ]}
                    />

                    <ExpandableExample title="Security log analysis — detecting intrusion attempts" icon={<SecurityIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            <code style={{ color: BRAND.goldBright }}>auth.log</code> and the SSH journal
                            unit record every login attempt. These commands help you spot brute-force attacks,
                            unauthorised access, and suspicious sudo usage.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ grep 'Failed password' /var/log/auth.log | tail -20",    comment: "Recent failed SSH password attempts" },
                                { cmd: "$ grep 'Failed password' /var/log/auth.log | awk '{print $(NF-3)}' | sort | uniq -c | sort -rn | head -10", comment: "Top 10 IPs with failed logins" },
                                { cmd: "$ grep 'Accepted password\\|Accepted publickey' /var/log/auth.log | tail -20", comment: "Successful logins" },
                                { cmd: "$ grep 'sudo' /var/log/auth.log | grep -v 'session'",      comment: "All sudo commands executed" },
                                { cmd: "$ journalctl -u sshd -p warning --since '24 hours ago'",   comment: "SSH warnings in the last 24 hours via journal" },
                                { cmd: "$ sudo lastb | head -20",                                  comment: "Failed login attempts from /var/log/btmp" },
                                { cmd: "$ sudo last | grep -v 'still logged' | head -20",          comment: "Recent successful login/logout events" },
                            ]}
                        />
                    </ExpandableExample>

                    <ExpandableExample title="Monitoring web server logs in real time" icon={<HttpIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            Web server access logs record every HTTP request. These commands let you
                            watch traffic live, find the most-requested URLs, and identify error spikes.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ sudo tail -f /var/log/nginx/access.log",                  comment: "Stream all Nginx requests in real time" },
                                { cmd: "$ sudo tail -f /var/log/nginx/error.log",                   comment: "Stream Nginx errors as they occur" },
                                { cmd: "$ awk '{print $1}' /var/log/nginx/access.log | sort | uniq -c | sort -rn | head -10", comment: "Top 10 client IPs by request count" },
                                { cmd: "$ awk '{print $9}' /var/log/nginx/access.log | sort | uniq -c | sort -rn", comment: "Count requests by HTTP status code" },
                                { cmd: "$ grep '\" 5' /var/log/nginx/access.log | tail -20",       comment: "All 5xx server errors from Nginx" },
                                { cmd: "$ awk '$9 == 404 {print $7}' /var/log/nginx/access.log | sort | uniq -c | sort -rn | head -10", comment: "Top 10 URLs returning 404" },
                            ]}
                        />
                    </ExpandableExample>

                    <ExpandableExample title="Writing a log monitoring alert script" icon={<NotificationsActiveIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            A simple shell script that scans a log file for errors and sends an email alert
                            if any are found. Combine with cron to run every 5 minutes.
                        </Typography>
                        <Box
                            sx={{
                                background: BRAND.codeBg,
                                border: `1px solid ${BRAND.border}`,
                                borderRadius: 2,
                                p: 2,
                                overflowX: "auto",
                            }}
                        >
                            {[
                                { line: "#!/bin/bash",                                              },
                                { line: "# monitor-errors.sh — alert on new critical errors",      isComment: true },
                                { line: "",                                                          },
                                { line: "LOG=/var/log/syslog",                                      },
                                { line: 'ALERT_EMAIL="admin@example.com"',                          },
                                { line: 'PATTERN="CRITICAL\\|emergency\\|kernel panic"',            },
                                { line: 'MARKER="/tmp/last-log-check.txt"',                         },
                                { line: "",                                                          },
                                { line: "# Get line number we last checked",                        isComment: true },
                                { line: 'LAST=$(cat "$MARKER" 2>/dev/null || echo 0)',              },
                                { line: 'CURRENT=$(wc -l < "$LOG")',                                },
                                { line: "",                                                          },
                                { line: "# Check new lines since last run",                         isComment: true },
                                { line: 'ERRORS=$(tail -n +"$LAST" "$LOG" | grep -iE "$PATTERN")', },
                                { line: "",                                                          },
                                { line: 'if [ -n "$ERRORS" ]; then',                                },
                                { line: '  echo "$ERRORS" | mail -s "[ALERT] Critical errors on $(hostname)" "$ALERT_EMAIL"', },
                                { line: "fi",                                                        },
                                { line: "",                                                          },
                                { line: 'echo "$CURRENT" > "$MARKER"',                              },
                            ].map((entry, i) => (
                                <Typography key={i} sx={{ fontFamily: "monospace", fontSize: 12, color: entry.isComment ? "rgba(255,255,255,0.3)" : BRAND.codeText, lineHeight: 1.85 }}>
                                    {entry.line || "\u00A0"}
                                </Typography>
                            ))}
                        </Box>
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 6 – WHAT YOU HAVE LEARNED
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="What You've Learned" />

                    <Grid container spacing={1.5}>
                        {[
                            "Where all major Linux log files live and what each one records",
                            "The difference between traditional plain-text logs and the systemd journal",
                            "How to view, follow, and search log files with tail, less, and grep",
                            "How to search inside compressed rotated log files with zgrep and zcat",
                            "All 8 syslog severity levels and how to filter by them in journalctl",
                            "How to filter the journal by service unit, time window, and priority",
                            "How to query the journal with combined filters and custom output formats",
                            "How to configure journal persistence and size limits in journald.conf",
                            "How logrotate works and how to write a custom rotation configuration",
                            "How to monitor processes, disk I/O, and network usage in real time",
                            "How to analyse auth.log to detect brute-force and intrusion attempts",
                            "How to monitor web server access logs and count requests by status code",
                            "How to write a cron-based log monitoring alert script",
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1, flexShrink: 0 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.6 }}>
                                        {item}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 7 – RESOURCES
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 4 }}>
                    <SectionTitle icon={<LinkIcon />} label="Further Resources" />

                    <ResourceLink
                        href="https://man7.org/linux/man-pages/man1/journalctl.1.html"
                        label="journalctl(1) — Linux Man Page"
                        sub="Complete reference for every journalctl flag, filter, and output format option."
                    />
                    <ResourceLink
                        href="https://linuxize.com/post/how-to-view-log-files-in-linux/"
                        label="How to View Log Files — Linuxize"
                        sub="Beginner-friendly walkthrough of tail, less, grep, and journalctl for log viewing."
                    />
                    <ResourceLink
                        href="https://wiki.archlinux.org/title/Systemd/Journal"
                        label="Arch Wiki — systemd Journal"
                        sub="Comprehensive journal reference including persistence, filtering, and forwarding to syslog."
                    />
                    <ResourceLink
                        href="https://linux.die.net/man/8/logrotate"
                        label="logrotate(8) — Man Page"
                        sub="All logrotate configuration directives documented with examples."
                    />
                    <ResourceLink
                        href="https://www.digitalocean.com/community/tutorials/how-to-use-journalctl-to-view-and-manipulate-systemd-logs"
                        label="How to Use journalctl — DigitalOcean"
                        sub="Practical deep-dive into journalctl with production examples and output format options."
                    />
                    <ResourceLink
                        href="https://betterstack.com/community/guides/logging/how-to-view-and-configure-linux-logs-on-ubuntu-and-centos/"
                        label="Linux Log Management Guide — Better Stack"
                        sub="Covers traditional logs, journald, logrotate, and centralised logging with rsyslog."
                    />
                </Box>

                {/* ── Footer pro tip ── */}
                <Box
                    sx={{
                        mt: 2, p: 2.5,
                        border: `1px solid ${BRAND.border}`,
                        borderRadius: 2,
                        background: BRAND.goldGlow,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                    }}
                >
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0, mt: 0.1 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.7 }}>
                        <strong style={{ color: BRAND.white }}>Pro tip for juniors:</strong> When something
                        breaks, your first three commands should always be:{" "}
                        <code style={{ color: BRAND.goldBright }}>journalctl -xe</code> (recent errors with
                        context),{" "}
                        <code style={{ color: BRAND.goldBright }}>journalctl -u servicename -n 50</code>{" "}
                        (last 50 lines from the broken service), and{" "}
                        <code style={{ color: BRAND.goldBright }}>dmesg | tail -20</code> (recent kernel
                        messages). Nine times out of ten, the answer is in one of these three outputs.
                    </Typography>
                </Box>

            </Container>
        </Box>
    );
}
